import invariant from 'invariant';
import { css, styled } from 'styled-components';

import { TagSimple } from '#/components/Tag/TagSimple';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nFn } from '#/i18n/types/I18nFn';
import {
  APPLICATION_STATUSES_ALL,
  RSVP_STATUSES,
  getColor as getStatusColor,
} from '#/modules/orgDashboard/constants/ats';
import { colors, mediaRetina } from '#/theme/theme';
import { ListingStatus, STATUSES } from '#/utils/constants/general/statuses';

export function ALL_STATUSES(
  i18n: I18nFn,
  listingType?: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT',
) {
  return {
    ...STATUSES(i18n),
    ...APPLICATION_STATUSES_ALL(i18n, listingType),
    ...RSVP_STATUSES(i18n),
    NEW: i18n('New Application'),
    INACTIVE: i18n('Inactive'),
    ACTIVE: i18n('Active'),
    // resume recommender statuses:
    ADDED_NOTE: i18n('Added Note'),
    CONTACTED_SUGG_APPL: i18n('Contacted'),
    RATING_CHANGED: i18n('Ranking Updated'),
    RECOMMENDED: i18n('Recommended On'),
  } as const;
}

export type AllStatusType = keyof ReturnType<typeof ALL_STATUSES>;

const StyledStatus = styled.div<{
  $color: string;
  $indicatorStyle?: 'outline' | 'solid';
}>`
  position: relative;
  font-size: 14px;
  letter-spacing: 1.8px;
  color: ${colors.lightContentGrey};
  text-transform: uppercase;
  line-height: 1.35;
  padding-left: 21px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 4px;
    border-radius: 50%;
    ${(props) =>
      props.$indicatorStyle === 'outline'
        ? css`
            width: 8px;
            height: 8px;
            border: solid 1px ${props.$color};
            ${mediaRetina} {
              border-width: 1.5px;
            }
          `
        : css`
            width: 11px;
            height: 11px;
            background-color: ${props.$color};
          `};
  }
`;

// return the first application status
const getApplicationStatus = (
  i18n: I18nFn,
  status: readonly string[] | string,
  listingType?: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT',
) => {
  // TODO: Fix this the next time the file is edited.
  // @ts-expect-error TS(2322): Type 'string | readonly string[]' is not assignabl... Remove this comment to see the full error message
  // eslint-disable-next-line no-param-reassign
  if (!Array.isArray(status)) status = [status];
  // @ts-expect-error TS(2339): Property 'find' does not exist on type 'string | r... Remove this comment to see the full error message
  return status.find(
    (s: string) => s in APPLICATION_STATUSES_ALL(i18n, listingType),
  );
};

type Props = {
  listingStatus: readonly string[] | string;
  asTag?: boolean;
  forceSolid?: boolean;
  colorOverride?: string | null;
  textColorOverride?: string | null;
  listingType?: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
};

export function ListingStatusLabel({
  listingStatus,
  asTag,
  forceSolid,
  colorOverride,
  textColorOverride,
  listingType,
  ...props
}: Props) {
  const { i18n } = useI18n();

  let status: AllStatusType | undefined;
  let color = colors.alertRed;
  let textColor = 'white';
  const indicatorStyle = forceSolid ? 'outline' : 'solid';

  if (listingStatus.includes('PUBLISHED')) {
    status = ListingStatus.PUBLISHED;
    color = '#68BD53';
  } else if (listingStatus.includes('REJECTED')) {
    status = ListingStatus.REJECTED;
    color = colors.brandYellow;
    textColor = colors.darkContentGrey;
  } else if (listingStatus.includes('DRAFT')) {
    status = ListingStatus.DRAFT;
    color = colors.selectionGrey;
    textColor = colors.darkContentGrey;
  } else if (listingStatus.includes('PENDING')) {
    status = ListingStatus.PENDING;
    color = '#C6BBD5';
    textColor = colors.darkContentGrey;
  } else if (listingStatus.includes('INVOICE')) {
    status = ListingStatus.INVOICE;
    color = colors.brandGreen;
  } else if (listingStatus.includes('ARCHIVED')) {
    status = ListingStatus.ARCHIVED;
    color = '#D0021B';
  } else if (listingStatus.includes('FROZEN')) {
    status = ListingStatus.FROZEN;
  } else if (listingStatus.includes('HIDDEN')) {
    status = ListingStatus.HIDDEN;
    color = '#9B489B';
  } else if (listingStatus.includes('PULLED')) {
    status = ListingStatus.PULLED;
  } else if (listingStatus.includes('DELETED')) {
    status = ListingStatus.DELETED;
    color = colors.decorativePlum;
  } else if (listingStatus.includes('CHECKOUT')) {
    status = ListingStatus.CHECKOUT;
    color = '#F7C702';
    textColor = colors.darkContentGrey;
  } else if (listingStatus.includes('SPAM')) {
    status = ListingStatus.SPAM;
  } else if (listingStatus.includes('APPROVED')) {
    status = ListingStatus.APPROVED;
    color = colors.brandGreen;
  } else if (getApplicationStatus(i18n, listingStatus, listingType)) {
    const statusColor = getStatusColor(
      getApplicationStatus(i18n, listingStatus, listingType) ?? 'APPLIED',
    );
    color = statusColor.bg;
    textColor = statusColor.color;
  } else if (listingStatus === 'ACTIVE') {
    color = colors.brandGreen;
  } else if (listingStatus === 'INACTIVE') {
    color = colors.elementGrey;
    textColor = colors.darkContentGrey;
  } else if (listingStatus === 'NEW') {
    color = colors.lemon;
    textColor = colors.darkContentGrey;
    status = 'NEW';
  } else if (listingStatus === 'ADDED_NOTE') {
    color = colors.brandGreen;
    textColor = colors.white;
    status = 'ADDED_NOTE';
  } else if (listingStatus === 'CONTACTED_SUGG_APPL') {
    color = colors.brandBlue;
    textColor = colors.white;
    status = 'CONTACTED_SUGG_APPL';
  } else if (listingStatus === 'RATING_CHANGED') {
    color = colors.brightPlum;
    textColor = colors.white;
    status = 'RATING_CHANGED';
  } else if (listingStatus === 'RECOMMENDED') {
    color = colors.decorativePlum;
    textColor = colors.white;
    status = 'RECOMMENDED';
  } else if (
    typeof listingStatus === 'string' &&
    Object.keys(RSVP_STATUSES(i18n)).includes(listingStatus)
  ) {
    const rsvpStatusColors = getStatusColor(
      listingStatus as keyof typeof RSVP_STATUSES,
    );
    color = rsvpStatusColors.bg;
    textColor = rsvpStatusColors.color;
  }

  if (
    typeof listingStatus === 'string' &&
    Object.keys(ALL_STATUSES(i18n, listingType)).includes(listingStatus)
  ) {
    status = listingStatus as AllStatusType;
  }

  if (colorOverride) {
    color = colorOverride;
  }
  if (textColorOverride) {
    textColor = textColorOverride;
  }

  invariant(status, 'Invalid status');

  return asTag ? (
    <TagSimple
      $bg={color}
      $borderColor={color}
      $color={textColor}
      $textTransform="uppercase"
      data-qa-id="listing-status"
    >
      {ALL_STATUSES(i18n, listingType)[status]}
    </TagSimple>
  ) : (
    <StyledStatus
      $color={color}
      $indicatorStyle={indicatorStyle}
      data-qa-id="listing-status"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {ALL_STATUSES(i18n, listingType)[status]}
    </StyledStatus>
  );
}
